import React, { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import { Col, Row, Button } from "reactstrap";
import { v4 } from "uuid";

export default function ImageCropper(props) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(3);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [base64Image, setBase64Image] = useState("");
  const localCropedImageUrl = croppedImage ? croppedImage.url : null;

  useEffect(() => {
    setBase64Image(props.defaultImage);
  }, [props.defaultImage]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const showCroppedImage = useCallback(async () => {
    try {
      const croppedImage = await getCroppedImg(
        base64Image,
        croppedAreaPixels,
        rotation
      );

      setCroppedImage(croppedImage);
      props.onImageCropped(croppedImage);
    } catch (e) {
      console.error(e);
    }
  }, [croppedAreaPixels, rotation]);

  return (
    <Row>
      <Col>
        <Row className="mt-4 mb-2">
          <Col md={4}>
            <p>Cor selecionada:</p>
          </Col>
          {(localCropedImageUrl || props.defaultCroppedImage) && (
            <img
              src={localCropedImageUrl || props.defaultCroppedImage}
              style={{ width: 30, height: 30, borderRadius: 100 }}
            />
          )}
          <Col></Col>
        </Row>
        {base64Image && (
          <>
            <Row className="mb-2">
              <Col>
                <div style={{ position: "relative", width: 400, height: 400 }}>
                  <Cropper
                    image={base64Image}
                    crop={crop}
                    rotation={rotation}
                    zoom={zoom}
                    aspect={4 / 4}
                    cropSize={{ width: 40, height: 40 }}
                    onCropChange={setCrop}
                    onRotationChange={setRotation}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col className="d-flex justify-content-end mt-3 mb-5">
                <Button color="info" onClick={showCroppedImage}>
                  Selecionar cor
                </Button>
              </Col>
            </Row>
          </>
        )}
      </Col>
    </Row>
  );
}

async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues on CodeSandbox
      image.src = url;
    });

  function getRadianAngle(degreeValue) {
    return (degreeValue * Math.PI) / 180;
  }

  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  // set each dimensions to double largest dimension to allow for a safe area for the
  // image to rotate in without being clipped by canvas context
  canvas.width = safeArea;
  canvas.height = safeArea;

  // translate canvas context to a central location on image to allow rotating around the center.
  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.translate(-safeArea / 2, -safeArea / 2);

  // draw rotated image and store data.
  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );
  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  // set canvas width to final desired crop size - this will clear existing context
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // paste generated rotate image with correct offsets for x,y crop values.
  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
  );

  // As Base64 string
  // return canvas.toDataURL('image/jpeg');

  // As a blob
  return new Promise((resolve) => {
    canvas.toBlob((file) => {
      resolve({ filename: v4(), file, url: URL.createObjectURL(file) });
    }, "image/jpeg");
  });
}
